/**
 * Demo data load and clear (F9, PRD §11).
 *
 * Two actions with a plain warning: loading populates every entity with
 * invented records, clearing removes them. Clear carries a confirm step,
 * because it deletes — even though it only ever touches demo-prefixed records,
 * a person clicking it should mean it.
 *
 * The result count is reported after each action, so "load" is not a button
 * that appears to do nothing on a database that already has the demo set.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.14, PRD §11
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Kicker } from '../../../components/ui/Kicker';
import { Note } from '../../../components/ui/Note';
import { useClearDemoData, useLoadDemoData } from '../api/admin.queries';

export function DemoControls(): React.JSX.Element {
  const { t } = useTranslation();
  const load = useLoadDemoData();
  const clear = useClearDemoData();
  const [confirmingClear, setConfirmingClear] = useState(false);

  const busy = load.isPending || clear.isPending;

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">{t('admin.demo.title')}</Kicker>
      </CardHead>
      <CardBody>
        <Note>{t('admin.demo.warning')}</Note>

        <div className="mt-s3 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              load.mutate();
            }}
          >
            {load.isPending ? t('admin.demo.loading') : t('admin.demo.load')}
          </Button>

          {confirmingClear ? (
            <>
              <span className="text-body text-sm">{t('admin.demo.confirmClear')}</span>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  clear.mutate(undefined, {
                    onSettled: () => {
                      setConfirmingClear(false);
                    },
                  });
                }}
              >
                {clear.isPending ? t('admin.demo.clearing') : t('admin.demo.confirmYes')}
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setConfirmingClear(false);
                }}
              >
                {t('actions.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setConfirmingClear(true);
              }}
            >
              {t('admin.demo.clear')}
            </Button>
          )}
        </div>

        {load.isSuccess && (
          <p role="status" className="text-sem-ok mt-s3 text-sm">
            {t('admin.demo.loaded', { count: load.data.affected })}
          </p>
        )}
        {clear.isSuccess && (
          <p role="status" className="text-body mt-s3 text-sm">
            {t('admin.demo.cleared', { count: clear.data.affected })}
          </p>
        )}
        {(load.isError || clear.isError) && (
          <p role="alert" className="text-sem-bad mt-s3 text-sm">
            {t('admin.demo.error')}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
